// The APIs we call and the webhooks we get at Exotel (plan v2 §3.2, §10), and the helpers the API seeds share.
// `traffic` is how busy each one is in the mock (calls an hour inside the sending window).
import type { ApiSettings } from '../../schemas'
import type { ApiTraffic, VendorApiRecord } from '../state'

export const settings = (
  path: string,
  timeout_ms: number,
  retries: number,
  per_minute: number,
  enabled = true
): ApiSettings => ({
  path,
  timeout_ms,
  retries,
  per_minute,
  enabled,
})
export const hook = (path: string, enabled = true) => settings(path, 5000, 0, 600, enabled)
export const traffic = (per_hour: number, ms: number, error_rate = 0.003): ApiTraffic => ({
  per_hour,
  ms,
  error_rate,
})

export const EXOTEL_APIS: VendorApiRecord[] = [
  {
    id: 'ex-call-connect',
    vendor_id: 'v-exotel',
    name: 'Start an IVR call',
    explanation:
      'Exotel rings the person from one of our numbers and runs our one generic call flow, which asks our server what to play.',
    when_called:
      "Each time a campaign's IVR call node calls a person (and each retry), inside the calling window.",
    direction: 'call',
    method: 'POST',
    used_by: ['Workflow node "IVR call"'],
    settings: settings('/v1/Accounts/{sid}/Calls/connect', 8000, 1, 60),
    traffic: traffic(140, 310, 0.004),
  },
  {
    id: 'ex-bulk-campaign',
    vendor_id: 'v-exotel',
    name: 'Start a bulk call campaign',
    explanation: 'Exotel calls up to 5,000 numbers itself, with its own throttle, retries and schedule.',
    when_called: 'Not used yet: the portal places calls one by one. Switched on when audiences outgrow that.',
    direction: 'call',
    method: 'POST',
    used_by: ['Campaigns (later)'],
    settings: settings('/v2/accounts/{sid}/campaigns', 15000, 0, 10, false),
    traffic: traffic(0, 600),
  },
  {
    id: 'ex-numbers',
    vendor_id: 'v-exotel',
    name: 'List our numbers',
    explanation: 'The phone numbers (ExoPhones) on our Exotel account, with their type and region.',
    when_called: 'When someone presses "Sync from Exotel" on IVR numbers, and once a night.',
    direction: 'call',
    method: 'GET',
    used_by: ['Page "IVR numbers" → Sync from Exotel'],
    settings: settings('/v2_beta/Accounts/{sid}/IncomingPhoneNumbers', 10000, 2, 10),
    traffic: traffic(0.5, 420),
  },
  {
    id: 'ex-number-buy',
    vendor_id: 'v-exotel',
    name: 'Buy a number',
    explanation:
      'Lists the numbers Exotel offers (140-series, 1600-series or normal, by region) and buys the one picked for our account.',
    when_called: 'When someone opens "Buy a number" on IVR numbers and picks one.',
    direction: 'call',
    method: 'POST',
    used_by: ['Page "IVR numbers" → Buy a number'],
    settings: settings('/v2_beta/Accounts/{sid}/IncomingPhoneNumbers', 15000, 0, 10),
    traffic: traffic(0, 900),
  },
  {
    id: 'ex-wa-send',
    vendor_id: 'v-exotel',
    name: 'Send a WhatsApp message',
    explanation:
      "Exotel sends one approved template from one of our WhatsApp numbers; the button carries the lender's link or the Apply now link.",
    when_called: "Each time a campaign's WhatsApp node sends, inside the sending window.",
    direction: 'call',
    method: 'POST',
    used_by: ['Workflow node "WhatsApp"'],
    settings: settings('/v2/accounts/{sid}/messages', 10000, 2, 300),
    traffic: traffic(90, 450, 0.012),
  },
  {
    id: 'ex-wa-template-create',
    vendor_id: 'v-exotel',
    name: 'Create a WhatsApp template',
    explanation:
      'Sends a new template (name, language, category, body, link button) through Exotel to Meta for approval.',
    when_called: 'When someone saves a new template on WhatsApp templates.',
    direction: 'call',
    method: 'POST',
    used_by: ['Page "WhatsApp templates" → Create'],
    settings: settings('/v2/accounts/{sid}/templates', 15000, 0, 10),
    traffic: traffic(0.2, 700),
  },
  {
    id: 'ex-wa-template-list',
    vendor_id: 'v-exotel',
    name: 'List our templates (Sync)',
    explanation:
      "Every template of our WhatsApp account with its status, including the ones made in Meta's WhatsApp Manager. Not in Exotel's public docs yet: asked (reminder 73).",
    when_called: 'Every hour, and when someone presses "Sync from Exotel" on WhatsApp templates.',
    direction: 'call',
    method: 'GET',
    used_by: ['Page "WhatsApp templates" (Sync from Exotel)'],
    settings: settings('/v2/accounts/{sid}/templates', 10000, 2, 30),
    traffic: traffic(2, 380),
  },
  {
    id: 'ex-hook-prompt',
    vendor_id: 'v-exotel',
    name: 'What to play',
    explanation:
      "Exotel's call flow asks us what to play; we answer with the campaign's voice message (an audio link or the words to speak).",
    when_called: 'At the start of every answered call.',
    direction: 'webhook',
    method: 'GET',
    used_by: ['Workflow node "IVR call" (voice message)'],
    settings: hook('/hooks/exotel/prompt'),
    traffic: traffic(75, 40, 0.001),
  },
  {
    id: 'ex-hook-digits',
    vendor_id: 'v-exotel',
    name: 'Digit pressed',
    explanation:
      'Exotel tells us which digit the person pressed, with our run id, so the workflow takes the matching port.',
    when_called: 'After every answered call.',
    direction: 'webhook',
    method: 'GET',
    used_by: ['Workflow node "IVR call" (digit map)'],
    settings: hook('/hooks/exotel/digits'),
    traffic: traffic(70, 35, 0.001),
  },
  {
    id: 'ex-hook-call-status',
    vendor_id: 'v-exotel',
    name: 'Call result',
    explanation: 'Answered, busy, no answer or failed, for every call we started.',
    when_called: 'At the end of every call.',
    direction: 'webhook',
    method: 'POST',
    used_by: ['Workflow node "IVR call" (retries)', 'Campaign results'],
    settings: hook('/hooks/exotel/call-status'),
    traffic: traffic(140, 30, 0.001),
  },
  {
    id: 'ex-hook-wa-status',
    vendor_id: 'v-exotel',
    name: 'WhatsApp delivered / read',
    explanation:
      'Sent, delivered, read or failed, for every message; Exotel tries again for up to an hour when we do not answer. Each send carries this address itself (status_callback), so no dashboard setting is needed.',
    when_called: 'Several times for each message we send.',
    direction: 'webhook',
    method: 'POST',
    used_by: ['Campaign results', 'WhatsApp numbers (quality)'],
    settings: hook('/hooks/exotel/wa-status'),
    traffic: traffic(250, 25, 0.002),
  },
  {
    id: 'ex-hook-wa-reply',
    vendor_id: 'v-exotel',
    name: 'WhatsApp reply (STOP)',
    explanation:
      'A reply from the person; STOP puts them on the suppression list at once. Exotel: WhatsApp → Webhooks → Number Level Webhook on each number, "incoming message" → this address; when the screen takes one address for both kinds, use /hooks/exotel/<secret>/wa (delivery reports and replies, counted on both rows).',
    when_called: 'When a person replies to a message.',
    direction: 'webhook',
    method: 'POST',
    used_by: ['Suppression list', 'Campaign results'],
    settings: hook('/hooks/exotel/wa-reply'),
    traffic: traffic(6, 30, 0.001),
  },
]
